import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { ContractPartyField } from '../../services/Contract';
import { useI18n } from '../../i18n';
import { cn } from '../../lib/utils';

interface ContractFormMessageProps {
  parties: ContractPartyField[];
  otherInfo: ContractPartyField[];
  onSubmit: (parties: ContractPartyField[], otherInfo: ContractPartyField[]) => void;
  disabled?: boolean;
  /** Already sent: show the values that were used instead of the form */
  submitted?: boolean;
}

/** A pre-filled date ("28/09/2026", "2026-09-28") as the yyyy-mm-dd a date input needs, or '' */
const toIsoDate = (value?: string) => {
  const v = (value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = v.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return '';
};

const initialValue = (field: ContractPartyField) => {
  const value = field.userResponse ?? field.pre_fill ?? '';
  return field.type === 'date' ? toIsoDate(value) || value : value;
};

const inputClass =
  'w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-brand placeholder:text-muted-foreground/60 disabled:opacity-70';

/**
 * The details QAgent needs before drafting: parties (required) and a few
 * optional fields, pre-filled from the description. Empty optional fields sit
 * behind "More details"; once submitted it collapses to a short summary.
 */
export const ContractFormMessage: React.FC<ContractFormMessageProps> = ({
  parties,
  otherInfo,
  onSubmit,
  disabled,
  submitted,
}) => {
  const { t } = useI18n();
  const cf = t.qagentChat.contractForm;
  const [partyValues, setPartyValues] = useState<ContractPartyField[]>(
    parties.map(p => ({ ...p, userResponse: initialValue(p) })),
  );
  const [otherValues, setOtherValues] = useState<ContractPartyField[]>(
    otherInfo.map(p => ({ ...p, userResponse: initialValue(p) })),
  );
  // Fields shown without expanding: every pre-filled one
  const [prefilled] = useState(() => new Set(otherInfo.filter(p => p.pre_fill?.trim()).map(p => p.label)));
  const [showAll, setShowAll] = useState(prefilled.size === 0);

  const updateParty = (idx: number, value: string) =>
    setPartyValues(prev => prev.map((p, i) => (i === idx ? { ...p, userResponse: value } : p)));
  const updateOther = (idx: number, value: string) =>
    setOtherValues(prev => prev.map((p, i) => (i === idx ? { ...p, userResponse: value } : p)));

  const filledRequired = partyValues.filter(p => (p.userResponse || '').trim().length > 0).length;
  const totalRequired = partyValues.length;
  const allFilled = filledRequired === totalRequired;
  const hiddenCount = otherValues.filter(p => !prefilled.has(p.label)).length;

  const handleSubmit = () => {
    if (!allFilled) return;
    onSubmit(partyValues, otherValues);
  };

  const renderField = (field: ContractPartyField, idx: number, onChange: (v: string) => void, required: boolean) => {
    const id = `cf-${required ? 'p' : 'o'}-${idx}`;
    const value = field.userResponse || '';
    return (
      <div key={id} className={cn('space-y-1', field.type === 'long_text' && 'sm:col-span-2')}>
        <label htmlFor={id} className="flex items-center gap-1 text-xs font-medium text-foreground" title={field.promptHint}>
          {field.label}
          {required && <span className="text-destructive">*</span>}
        </label>
        {field.type === 'long_text' ? (
          <textarea
            id={id}
            className={cn(inputClass, 'resize-y')}
            rows={2}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={field.promptHint}
            disabled={disabled}
          />
        ) : (
          <input
            id={id}
            type={field.type === 'date' && (!value || toIsoDate(value)) ? 'date' : 'text'}
            className={inputClass}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={field.promptHint}
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  // Submitted: the values that were used, not the whole form again
  if (submitted) {
    const filled = [...partyValues, ...otherValues].filter(f => f.userResponse?.trim());
    return (
      <div className="rounded-xl border border-border bg-background px-4 py-3 text-sm">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-brand" />
          {cf.submitted}
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
          {filled.map((f, i) => (
            <div key={i} className="flex min-w-0 gap-1.5">
              <dt className="shrink-0 text-muted-foreground">{f.label}:</dt>
              <dd className="truncate" title={f.userResponse}>{f.userResponse}</dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand" />
          <h3 className="text-sm font-semibold text-foreground">{cf.title}</h3>
        </div>
        <span className="text-xs text-muted-foreground">{cf.required(filledRequired, totalRequired)}</span>
      </div>

      <div className="p-4 space-y-4">
        {partyValues.length > 0 && (
          <section className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{cf.parties}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {partyValues.map((field, idx) => renderField(field, idx, v => updateParty(idx, v), true))}
            </div>
          </section>
        )}

        {otherValues.length > 0 && (
          <section className="space-y-2 pt-3 border-t border-border/60">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {cf.additionalDetails}
              <span className="ml-1 text-[10px] font-normal normal-case tracking-normal text-muted-foreground/60">{cf.optional}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {otherValues.map((field, idx) =>
                showAll || prefilled.has(field.label) ? renderField(field, idx, v => updateOther(idx, v), false) : null,
              )}
            </div>
            {hiddenCount > 0 && prefilled.size > 0 && (
              <button
                type="button"
                onClick={() => setShowAll(v => !v)}
                className="flex items-center gap-1 text-xs font-medium text-brand hover:underline"
              >
                {showAll ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                {showAll ? cf.fewerDetails : cf.moreDetails(hiddenCount)}
              </button>
            )}
          </section>
        )}
      </div>

      <div className="px-4 py-3 border-t bg-muted/30 flex items-center justify-end gap-2">
        <Button onClick={handleSubmit} disabled={disabled || !allFilled} className="bg-brand text-white hover:bg-brand/90">
          {cf.generate}
        </Button>
      </div>
    </div>
  );
};
